import { ChangeTracker } from "./changeTracker";
import { SyncQueueStore } from "../stores/syncQueueStore";
import { PreOrderStore } from "../stores/preOrderStore";
import type { PreOrderFlow } from "@/app/api/types/preOrder";

export interface BatchValidateRequest {
  changes: Array<{
    type: "create" | "update" | "delete";
    pre_order_id: string;
    flow_id: string;
    flow_data?: Partial<PreOrderFlow>;
  }>;
}

export interface BatchValidateResponse {
  created: PreOrderFlow[];
  updated: PreOrderFlow[];
  deleted: string[];
  id_mapping: Record<string, string>; // temp_id -> real_id
  restored?: PreOrderFlow[]; // Flows restored from soft-delete
  skipped_deletes?: string[]; // Flows already deleted (idempotent)
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: Array<{ changeId: string; error: string }>;
}

export class BatchProcessor {
  constructor(
    private changeTracker: ChangeTracker,
    private syncQueue: SyncQueueStore,
    private preOrderStore: PreOrderStore,
  ) {}

  // Build batch request for a pre-order
  async buildBatchRequest(preOrderId: string): Promise<BatchValidateRequest> {
    // Get all pending changes for this pre-order
    const pendingChanges = await this.changeTracker.getChangesByPreOrderIds([
      preOrderId,
    ]);

    const changes: BatchValidateRequest["changes"] = [];

    // Process each change
    for (const change of pendingChanges) {
      if (change.entityType === "flow") {
        if (change.operation === "create") {
          changes.push({
            type: "create",
            pre_order_id: preOrderId,
            flow_id: change.entityId,
            flow_data: change.data,
          });
        } else if (change.operation === "update") {
          changes.push({
            type: "update",
            pre_order_id: preOrderId,
            flow_id: change.entityId,
            flow_data: change.data,
          });
        } else if (change.operation === "delete") {
          changes.push({
            type: "delete",
            pre_order_id: preOrderId,
            flow_id: change.entityId,
          });
        }
      }
    }

    return { changes };
  }

  // Process successful sync response
  async processSyncResponse(
    preOrderId: string,
    date: string,
    response: BatchValidateResponse,
    syncedChangeIds?: string[], // IDs of changes that were actually synced
  ): Promise<void> {
    // Update local cache with server data
    const preOrder = await this.preOrderStore.get(date, preOrderId);
    if (!preOrder) {
      return;
    }

    // Apply ID mappings (temp ID -> real ID) to flows in cache
    if (preOrder.flows) {
      for (const flow of preOrder.flows) {
        if (response.id_mapping[flow.id]) {
          flow.id = response.id_mapping[flow.id];
        }
      }
    }

    // CRITICAL: Update any pending changes in the queue to use new real IDs
    // This handles the case where user edits a newly created flow while sync is in progress
    for (const [tempId, realId] of Object.entries(response.id_mapping)) {
      await this.syncQueue.updateFlowId(tempId, realId);
    }

    // Apply updated flows
    for (const updated of response.updated) {
      const flowIndex =
        preOrder.flows?.findIndex((f) => f.id === updated.id) ?? -1;
      if (flowIndex >= 0 && preOrder.flows) {
        preOrder.flows[flowIndex] = updated;
      }
    }

    // Apply created flows (replace temp flows with server response)
    for (const created of response.created) {
      const tempId = Object.entries(response.id_mapping).find(
        ([_, realId]) => realId === created.id,
      )?.[0];

      if (tempId) {
        const tempIndex =
          preOrder.flows?.findIndex((f) => f.id === tempId) ?? -1;
        if (tempIndex >= 0 && preOrder.flows) {
          preOrder.flows[tempIndex] = created;
        }
      }
    }

    // Remove deleted flows
    for (const deletedId of response.deleted) {
      preOrder.flows = preOrder.flows?.filter((f) => f.id !== deletedId);
    }

    // Update preOrder's updated_at
    preOrder.updated_at = new Date().toISOString();

    // Save updated pre-order
    await this.preOrderStore.set(date, preOrder);

    // CRITICAL: Only remove the changes that were actually synced
    // Do NOT remove all changes for this pre-order, as new changes may have been made during sync
    if (syncedChangeIds && syncedChangeIds.length > 0) {
      await this.syncQueue.remove(syncedChangeIds);
    } else {
      // Fallback: remove all changes for this pre-order (old behavior)
      await this.syncQueue.removeByPreOrderId(preOrderId);
    }
  }

  // Handle sync failure
  async handleSyncFailure(
    preOrderId: string,
    changeIds: string[],
  ): Promise<void> {
    for (const id of changeIds) {
      await this.syncQueue.markFailed(id);
    }
  }

  // Get all pre-orders with pending changes
  async getPreOrdersToSync(): Promise<string[]> {
    const dirty = await this.changeTracker.getDirtyPreOrderIds();
    return Array.from(dirty);
  }
}
